import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import BackLink from "../components/BackLink";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Stars, TeacherBadge } from "./Teachers";
import {
  Globe2, Languages, Clock, GraduationCap, Star,
  CalendarPlus, MessageCircle, Users, BookOpen, Heart, Sparkles,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const MEDIA = (key) => `${process.env.REACT_APP_BACKEND_URL}/api/teacher-app/media/${key}`;

const STYLE_ICONS = { default: Sparkles };

export default function TeacherProfile() {
  const { tx, t: tr } = useLanguage();
  const { teacherId } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [review, setReview] = useState({ stars: 5, text: "" });
  const [booking, setBooking] = useState(null); // {type, preferred_time, note} when dialog open

  const load = () => {
    api.get(`/teachers/${teacherId}`).then((r) => setT(r.data)).catch(() => setNotFound(true));
  };
  useEffect(load, [teacherId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (notFound) return (
    <div className="max-w-3xl mx-auto hifz-card text-center p-10">
      <p className="text-sm text-muted-foreground">{tr("teacherProfile.thisTeacherProfileIsnT")}</p>
      <Link to="/app/teachers" className="btn-ghost text-xs mt-3 inline-flex">{tr("teacherProfile.backToDirectory")}</Link>
    </div>
  );
  if (!t) return <div className="max-w-3xl mx-auto hifz-card h-96 animate-pulse-soft bg-sand-200/50" />;

  const isMe = user?.user_id === t.user_id;

  const submitBooking = async () => {
    try {
      // datetime-local has no timezone; send an absolute instant so both sides see the right time.
      const when = booking.preferred_time ? new Date(booking.preferred_time) : null;
      await api.post("/bookings", {
        teacher_id: t.user_id, ...booking,
        preferred_time: when && !isNaN(when) ? when.toISOString() : null,
      });
      toast.success(tr("teacherProfile.requestSentToTheyVe", { name: t.name }));
      setBooking(null);
    } catch (e) { toast.error(e?.response?.data?.detail || tr("teacherProfile.couldnTSendRequest")); }
  };

  const submitReview = async () => {
    try {
      const r = await api.post(`/teachers/${t.user_id}/review`, review);
      toast.success(tr("teacherProfile.jazakallahKhairForYourReview"));
      setReview({ stars: 5, text: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tr("teacherProfile.couldnTSubmitReview")); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="teacher-profile-page">
      <BackLink to="/app/teachers" label={tr("teacherProfile.allTeachers")} className="btn-ghost text-xs inline-flex" testid="profile-back" />

      {/* Professional header */}
      <section className="hifz-card" data-testid="profile-header">
        <div className="flex flex-wrap items-start gap-4">
          <div className="h-20 w-20 rounded-3xl bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold text-3xl">
            {(t.name || "T")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-semibold text-3xl text-forest-700">{t.name}</h1>
              {t.status === "featured" && <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/15 text-purple-700 font-semibold uppercase">{tr("teachers.featured")}</span>}
              {t.hourly_rate != null && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gold-500/15 text-gold-600 font-semibold" data-testid="profile-rate">
                  ${t.hourly_rate}/hr
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Globe2 className="h-3.5 w-3.5" />{t.country}{t.city ? ` · ${t.city}` : ""}</span>
              <span className="inline-flex items-center gap-1"><Languages className="h-3.5 w-3.5" />{t.languages.join(", ")}</span>
              {t.timezone && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t.timezone}</span>}
              {t.years_teaching && <span className="inline-flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" />{tr("teacherProfile.years", { years_teaching: t.years_teaching })}</span>}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Stars value={t.rating} count={t.reviews_count} />
              {t.badges.map((b) => <TeacherBadge key={b} badge={b} />)}
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          {!isMe && (
            <button onClick={() => setBooking({ type: "trial", preferred_time: "", note: "" })}
              className="btn-primary text-xs" data-testid="book-trial-btn">
              <CalendarPlus className="h-3.5 w-3.5" /> {tr("teacherProfile.bookTrialLesson")}
            </button>
          )}
          <button onClick={() => toast.info(tr("teacherProfile.directMessagingOpensInThe"))}
            className="btn-ghost text-xs" data-testid="message-teacher-btn">
            <MessageCircle className="h-3.5 w-3.5" /> {tr("teacherProfile.messageTeacher")}
          </button>
        </div>
      </section>

      {booking && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setBooking(null)}>
          <div className="w-full max-w-md rounded-2xl bg-sand-100 p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="booking-dialog">
            <h3 className="font-display font-semibold text-xl text-forest-700">{tr("teacherProfile.requestALessonWith", { name: t.name })}</h3>
            <label className="block text-xs font-semibold text-forest-700">{tr("teacherProfile.sessionType")}
              <select className="w-full mt-1 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" value={booking.type}
                onChange={(e) => setBooking((b) => ({ ...b, type: e.target.value }))} data-testid="booking-type">
                <option value="trial">{tr("teacherProfile.trialClassFreeIntro")}</option>
                <option value="one_to_one">{tr("teacherProfile.oneToOneLesson")}</option>
                <option value="family">{tr("teacherProfile.familySession")}</option>
                <option value="weekly_plan">{tr("teacherProfile.weeklyPlan")}</option>
                <option value="monthly_plan">{tr("teacherProfile.monthlyPlan")}</option>
              </select>
            </label>
            <label className="block text-xs font-semibold text-forest-700">{tr("teacherProfile.preferredTime")}
              <input type="datetime-local" className="w-full mt-1 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
                value={booking.preferred_time} onChange={(e) => setBooking((b) => ({ ...b, preferred_time: e.target.value }))} data-testid="booking-time" />
            </label>
            <label className="block text-xs font-semibold text-forest-700">{tr("teacherProfile.noteToTeacher")}
              <textarea rows={2} className="w-full mt-1 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
                placeholder={tr("teacherProfile.yourGoalsLevelAnythingHelpful")} value={booking.note}
                onChange={(e) => setBooking((b) => ({ ...b, note: e.target.value }))} data-testid="booking-note" />
            </label>
            {booking.type !== "trial" && t.hourly_rate != null && (
              <div className="rounded-xl border border-gold-500/30 bg-gold-500/5 p-3 text-xs text-foreground/75" data-testid="booking-rate-note">
                {tr("teacherProfile.billedAt")} <strong>${t.hourly_rate}/hour</strong>. {tr("teacherProfile.paymentSoon", { name: t.name.split(" ")[0] })}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setBooking(null)} className="btn-ghost text-xs" data-testid="booking-cancel">{tr("halaqa.cancel")}</button>
              <button onClick={submitBooking} className="btn-primary text-xs" data-testid="booking-submit">{tr("teacherProfile.sendRequest")}</button>
            </div>
          </div>
        </div>
      )}

      {/* About */}
      {t.bio && (
        <section className="hifz-card space-y-3" data-testid="profile-about">
          <h2 className="font-display font-semibold text-xl text-forest-700">{tr("teacherProfile.about")}</h2>
          <p className="text-sm text-foreground/80">{t.bio}</p>
        </section>
      )}

      {/* Recitation + demo */}
      {(t.recitation?.key || t.demo?.key) && (
        <section className="hifz-card space-y-4" data-testid="profile-media">
          <h2 className="font-display font-semibold text-xl text-forest-700">{tr("teacherProfile.hearSeeTheTeaching")}</h2>
          {t.recitation?.key && (
            <div>
              <div className="text-sm font-semibold text-forest-700">{tr("teacherProfile.sampleRecitation", { surah_name: t.recitation.surah_name, ayah_start: t.recitation.ayah_start, ayah_end: t.recitation.ayah_end })}</div>
              <audio controls src={MEDIA(t.recitation.key)} className="w-full mt-2" data-testid="profile-recitation" />
              {t.recitation.ai_score && (
                <div className="mt-2 grid grid-cols-4 gap-1.5 text-center">
                  {Object.entries(t.recitation.ai_score).map(([k, v]) => (
                    <div key={k} className="rounded bg-sand-200/70 px-1 py-1.5">
                      <div className={`text-sm font-semibold ${k === "overall" ? "text-gold-600" : "text-forest-700"}`}>{v}</div>
                      <div className="text-[8px] uppercase text-muted-foreground">{k}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {t.demo?.key && (
            <div>
              <div className="text-sm font-semibold text-forest-700 mb-1">{tr("teacherProfile.teachingDemonstration")}</div>
              <video controls className="w-full rounded-xl max-h-80" src={MEDIA(t.demo.key)} data-testid="profile-demo" />
            </div>
          )}
        </section>
      )}

      {/* Impact */}
      <section className="hifz-card" data-testid="profile-impact">
        <h2 className="font-display font-semibold text-xl text-forest-700">{tr("teacherProfile.impactOnThePlatform")}</h2>
        <div className="mt-3 grid grid-cols-3 gap-3 text-center">
          {[[Users, t.impact.students, tr("teacherProfile.impactStudents")], [BookOpen, t.impact.halaqas_led, tr("teacherProfile.impactHalaqasLed")], [Heart, t.impact.feedback_sent, tr("teacherProfile.impactFeedbackGiven")]].map(([Icon, v, label]) => (
            <div key={label} className="rounded-xl bg-sand-200/60 px-3 py-3">
              <Icon className="h-4 w-4 mx-auto text-gold-600" />
              <div className="mt-1 font-display font-semibold text-xl text-forest-700">{v}</div>
              <div className="text-[11px] text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="hifz-card space-y-4" data-testid="profile-reviews">
        <h2 className="font-display font-semibold text-xl text-forest-700">{tr("teacherProfile.reviews")}</h2>
        {t.reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">{tr("teacherProfile.noReviewsYetBeThe")}</p>
        ) : (
          <div className="space-y-3">
            {t.reviews.map((r, i) => (
              <div key={i} className="rounded-xl bg-sand-200/50 p-3" data-testid={`review-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-forest-700">{r.reviewer_name} <span className="text-[10px] text-muted-foreground capitalize">· {r.reviewer_role}</span></div>
                  <span className="inline-flex items-center gap-0.5 text-xs">
                    {Array.from({ length: r.stars }).map((_, j) => <Star key={j} className="h-3 w-3 fill-gold-500 text-gold-500" />)}
                  </span>
                </div>
                {r.text && <p className="mt-1 text-sm text-foreground/80">{tx(r.text)}</p>}
              </div>
            ))}
          </div>
        )}
        {!isMe && (
          <div className="border-t border-sand-200 pt-4 space-y-2">
            <div className="flex items-center gap-1" data-testid="review-stars-picker">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} onClick={() => setReview((r) => ({ ...r, stars: s }))} data-testid={`star-${s}`}>
                  <Star className={`h-5 w-5 ${s <= review.stars ? "fill-gold-500 text-gold-500" : "text-sand-300"}`} />
                </button>
              ))}
            </div>
            <textarea rows={2} value={review.text} onChange={(e) => setReview((r) => ({ ...r, text: e.target.value }))}
              placeholder={tr("teacherProfile.shareYourExperienceWithThis")}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="review-text" />
            <button onClick={submitReview} className="btn-primary text-xs" data-testid="review-submit">{tr("teacherProfile.submitReview")}</button>
          </div>
        )}
      </section>
    </div>
  );
}
